import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Check, ArrowLeft, Loader2, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { Field, Input, Textarea } from "@/components/Field";
import CheckoutDialog from "@/components/CheckoutDialog";
import Seo from "@/components/Seo";

export default function PackageCheckout() {
  const { id } = useParams();
  const { lang, pickL } = useI18n();
  const { user } = useAuth();
  const de = lang === "de";
  const nav = useNavigate();
  const [pkg, setPkg] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ project_name: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [invoice, setInvoice] = useState(null); // created invoice → open payment

  useEffect(() => {
    api.get("/packages").then((r) => {
      const found = (r.data || []).find((p) => p.id === id);
      if (found) { setPkg(found); setForm((f) => ({ ...f, project_name: pickL(found, "name") || "" })); }
      else setNotFound(true);
    }).catch(() => setNotFound(true));
  }, [id]); // eslint-disable-line

  const price = pkg ? Number(pkg.price_discounted || pkg.price || 0) : 0;

  const confirm = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/packages/${id}/order`, { project_name: form.project_name, notes: form.notes });
      toast.success(de ? "Bestellung erstellt" : "Order created");
      setInvoice({ id: data.invoice_id, total: data.total, number: data.number, currency: data.currency || "EUR" });
    } catch (e) { toast.error(apiErr(e)); setSubmitting(false); }
  };

  if (notFound) {
    return (
      <div className="container-x py-24 text-center">
        <p className="text-muted-foreground">{de ? "Paket nicht gefunden." : "Package not found."}</p>
        <button onClick={() => nav("/packages")} className="btn-ghost mt-4">{de ? "Zu den Paketen" : "Back to packages"}</button>
      </div>
    );
  }
  if (!pkg) return <div className="container-x py-24 flex justify-center"><Loader2 className="animate-spin text-primary" /></div>;

  return (
    <div className="container-x py-12 max-w-4xl" data-testid="package-checkout">
      <Seo title={`${pickL(pkg, "name")} — Checkout`} />
      <button onClick={() => nav("/packages")} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5 mb-6" data-testid="checkout-back">
        <ArrowLeft size={15} /> {de ? "Zurück zu den Paketen" : "Back to packages"}
      </button>
      <h1 className="font-display text-3xl font-black flex items-center gap-2 mb-8"><ShoppingBag size={26} className="text-primary" /> {de ? "Bestellung abschließen" : "Complete your order"}</h1>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Review */}
        <div className="lg:col-span-3 surface p-6" data-testid="checkout-package-review">
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="font-display font-bold text-xl" data-testid="checkout-package-name">{pickL(pkg, "name")}</h2>
            <div className="text-right">
              {pkg.price_discounted && <span className="text-muted-foreground line-through mr-2 text-sm">{pkg.price}€</span>}
              <span className="font-display text-2xl font-black" data-testid="checkout-package-price">{price}€</span>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{pickL(pkg, "desc")}</p>
          <p className="text-xs text-muted-foreground mt-1">{de ? "Gemäß § 19 UStG wird keine Umsatzsteuer berechnet." : "No VAT is charged pursuant to § 19 UStG."} · {de ? "Lieferzeit" : "Delivery"}: {pkg.delivery_time}</p>
          <ul className="space-y-2.5 mt-5">
            {(pickL(pkg, "features") || []).map((f, j) => (
              <li key={j} className="flex items-start gap-2 text-sm"><Check size={16} className="text-primary shrink-0 mt-0.5" /> {f}</li>
            ))}
          </ul>
        </div>

        {/* Order info + confirm */}
        <div className="lg:col-span-2 surface p-6 h-fit space-y-4">
          <h2 className="font-display font-bold text-lg">{de ? "Projektangaben" : "Project details"}</h2>
          <Field label={de ? "Projektname" : "Project name"}>
            <Input testid="checkout-project-name" value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} />
          </Field>
          <Field label={de ? "Anmerkungen (optional)" : "Notes (optional)"}>
            <Textarea testid="checkout-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder={de ? "Ihre Wünsche, Referenzen, Deadline …" : "Your requirements, references, deadline …"} />
          </Field>
          <div className="flex items-center justify-between text-sm border-t border-border pt-3">
            <span className="text-muted-foreground">{de ? "Gesamt" : "Total"}</span>
            <span className="font-display font-black text-lg">{price}€</span>
          </div>
          <button onClick={confirm} disabled={submitting || !!invoice} className="btn-primary w-full" data-testid="checkout-confirm">
            {submitting ? <Loader2 size={16} className="animate-spin" /> : (de ? "Bestellung bestätigen & bezahlen" : "Confirm order & pay")}
          </button>
          <p className="text-[11px] text-center text-muted-foreground">{de ? "Angemeldet als" : "Signed in as"} {user?.email}</p>
        </div>
      </div>

      {invoice && (
        <CheckoutDialog
          invoice={invoice}
          onClose={() => nav("/portal/orders")}
          onPaid={() => nav("/portal/invoices")}
        />
      )}
    </div>
  );
}
